import { useEffect, useState, useRef } from "react";
import { Bell, ChevronRight, ClipboardList, CreditCard, FileText, LayoutDashboard, ListTodo, CheckSquare, LogOut, Menu, PackageCheck, Search, Settings as Cog, Truck, Users, X, BarChart3, ShieldCheck, ShieldAlert, User, Palette, CheckCircle2, Factory, Send, Sparkles, ShoppingCart, Receipt, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { api } from "../../lib/api";
import { testid } from "../../lib/format";
import { formatRole, isAdmin, isSuperAdmin, canAccessNavModule, formatUserDepartments } from "../../lib/permissions";

const ALL_NAV = [
  { label: "Dashboard", icon: LayoutDashboard },
  { label: "Activities", icon: ListTodo },
  { label: "Tasks & Reports", icon: CheckSquare },
  { label: "Leads", icon: Sparkles },
  { label: "Procurement", icon: ShoppingCart },
  { label: "Clients", icon: Users },
  { label: "Orders", icon: ClipboardList },
  { label: "Design", icon: Palette },
  { label: "Approvals", icon: CheckCircle2 },
  { label: "Production", icon: Factory },
  { label: "Dispatch", icon: Send },
  { label: "Delivery", icon: Truck },
  { label: "Accounts", icon: Receipt },
  { label: "Reports", icon: BarChart3 },
  { label: "Users & Roles", icon: ShieldCheck },
];

export default function Shell({ user, page, setPage, onLogout, onOpenOrder, onOpenClient, onOpenLead, onNavigate, children }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);

  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      return localStorage.getItem("crm_sidebar_collapsed") === "true";
    } catch {
      return false;
    }
  });

  const toggleSidebar = () => {
    setIsCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("crm_sidebar_collapsed", String(next));
      } catch {}
      return next;
    });
  };

  const effectivelyCollapsed = isCollapsed && !menuOpen;
  const scrollAreaRef = useRef(null);

  // Scroll main content area back to top on page navigation
  useEffect(() => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = 0;
    }
  }, [page]);

  // Keyboard shortcut Ctrl+[ or Cmd+[ to toggle sidebar
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "[") {
        e.preventDefault();
        toggleSidebar();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const isUserAdmin = isAdmin(user);
  const userDeptsFormatted = formatUserDepartments(user);

  useEffect(() => {
    if (!profileOpen) return;
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [profileOpen]);

  useEffect(() => {
    let mounted = true;
    const load = () => api("get", "/notifications").then((r) => mounted && setNotifs(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 20000);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  useEffect(() => {
    if (!q) { setResults(null); return; }
    const handle = setTimeout(async () => {
      try {
        const r = await api("get", `/search?q=${encodeURIComponent(q)}`);
        setResults(r.data);
      } catch { /* noop */ }
    }, 220);
    return () => clearTimeout(handle);
  }, [q]);

  const go = (label) => { setPage(label); setMenuOpen(false); };
  const initials = (user?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const unread = notifs.filter((n) => !n.read).length;

  const markRead = async (id) => {
    await api("post", `/notifications/${id}/read`).catch(() => {});
    setNotifs((prev) => prev.map((n) => (n.notif_id === id ? { ...n, read: true } : n)));
  };

  // Evaluate all assigned departments together to construct the navigation list
  const currentNav = ALL_NAV.filter((item) => canAccessNavModule(item.label, user));

  return (
    <div className="app-shell" data-testid="app-shell">
      {/* Mobile Backdrop */}
      {menuOpen && (
        <div
          className="mobile-backdrop"
          data-testid="mobile-sidebar-backdrop"
          onClick={() => setMenuOpen(false)}
        />
      )}

      {/* 1. Fixed & Collapsible Sidebar */}
      <aside
        className={`sidebar ${effectivelyCollapsed ? "collapsed" : ""} ${menuOpen ? "mobile-open" : ""}`}
        data-testid="app-sidebar"
        aria-label="Sidebar Navigation"
      >
        {/* Stationary Brand Header with Logo & Toggle Button */}
        <div className="sidebar-brand">
          <div
            className="brand-logo-container"
            onClick={() => go(isUserAdmin ? "Dashboard" : "Activities")}
            style={{ cursor: "pointer" }}
            title="Pinaki Solutions CRM"
          >
            {effectivelyCollapsed ? (
              <div className="brand-collapsed-mark" title="Pinaki Solutions">
                <img
                  src="/pinaki_logo.png"
                  alt="Pinaki Solutions"
                  className="logo-mark-img"
                />
              </div>
            ) : (
              <img
                src="/pinaki_logo.png"
                alt="Pinaki Solutions"
                className="logo-full-img"
              />
            )}
          </div>

          <button
            type="button"
            className="sidebar-toggle-btn"
            onClick={toggleSidebar}
            title={effectivelyCollapsed ? "Expand sidebar (Ctrl+[)" : "Collapse sidebar (Ctrl+[)"}
            data-testid="sidebar-toggle-button"
            aria-label={effectivelyCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-expanded={!effectivelyCollapsed}
          >
            {effectivelyCollapsed ? <PanelLeftOpen size={17} /> : <PanelLeftClose size={17} />}
          </button>

          <button
            className="mobile-close"
            data-testid="mobile-close-button"
            onClick={() => setMenuOpen(false)}
            aria-label="Close mobile navigation"
          >
            <X size={18} />
          </button>
        </div>

        {/* Workspace Label / Divider */}
        {!effectivelyCollapsed ? (
          <div className="workspace-label">WORKSPACE</div>
        ) : (
          <div className="sidebar-divider" />
        )}

        {/* 2. Independently Scrollable Navigation Menu */}
        <nav className="sidebar-nav" data-testid="sidebar-nav">
          {currentNav.map(({ label, icon: Icon }) => {
            const isActive =
              page === label ||
              (page === "TaskDetail" && label === "Tasks & Reports") ||
              (page === "OrderDetail" && label === "Orders") ||
              (page === "DesignDetail" && label === "Design") ||
              (page === "ProductionDetail" && label === "Production") ||
              (page === "DispatchDetail" && label === "Dispatch") ||
              (page === "DeliveryDetail" && label === "Delivery") ||
              (page === "UserDetail" && label === "Users & Roles") ||
              (page === "LeadDetail" && label === "Leads") ||
              (page === "ClientDetail" && label === "Clients") ||
              (page === "ProcurementDetail" && label === "Procurement");

            return (
              <button
                key={label}
                className={`nav-item ${isActive ? "active" : ""} ${effectivelyCollapsed ? "collapsed" : ""}`}
                data-testid={`nav-${testid(label)}`}
                onClick={() => go(label)}
                title={label}
                aria-label={label}
              >
                <Icon size={17} className="nav-icon" />
                {!effectivelyCollapsed && <span className="nav-label">{label}</span>}
              </button>
            );
          })}
        </nav>

        {/* 3. Stationary Bottom Section with Settings and User Profile */}
        <div className="sidebar-bottom">
          {isSuperAdmin(user) && (
            <button
              className={`nav-item ${page === "Settings" ? "active" : ""} ${effectivelyCollapsed ? "collapsed" : ""}`}
              data-testid="nav-settings"
              onClick={() => go("Settings")}
              title="Settings"
              aria-label="Settings"
            >
              <Cog size={17} className="nav-icon" />
              {!effectivelyCollapsed && <span className="nav-label">Settings</span>}
            </button>
          )}

          <div
            className={`user-chip-clickable ${effectivelyCollapsed ? "collapsed" : ""}`}
            data-testid="sidebar-user-chip"
            title={`${user?.name || "User"} (${formatRole(user?.role)}) - Click to view My Profile`}
            onClick={() => go("Profile")}
          >
            <div className="avatar">{initials}</div>
            {!effectivelyCollapsed && (
              <div style={{ flex: 1, minWidth: 0 }}>
                <b>{user?.name}</b>
                <small>
                  {formatRole(user?.role)}
                  {userDeptsFormatted ? ` · ${userDeptsFormatted}` : ""}
                </small>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main Container */}
      <main className="main" data-testid="app-main">
        <header className="topbar">
          <button className="mobile-menu" data-testid="mobile-menu-button" onClick={() => setMenuOpen(true)}><Menu size={20} /></button>
          <div className="breadcrumbs">
            <span
              className="crumb"
              style={{ cursor: "pointer" }}
              onClick={() => go(isUserAdmin ? "Dashboard" : "Activities")}
            >
              Pinaki Solutions
            </span>
            <ChevronRight size={14} />
            <span className="crumb" style={page.includes("Detail") || page === "ApprovalPage" ? { cursor: "pointer", color: "var(--navy)" } : {}} onClick={() => {
              if (page === "TaskDetail") setPage("Tasks & Reports");
              if (page === "OrderDetail") setPage("Orders");
              if (page === "DesignDetail") setPage("Design");
              if (page === "ProductionDetail") setPage("Production");
              if (page === "DispatchDetail") setPage("Dispatch");
              if (page === "DeliveryDetail") setPage("Delivery");
              if (page === "UserDetail") setPage("Users & Roles");
              if (page === "LeadDetail") setPage("Leads");
              if (page === "ClientDetail") setPage("Clients");
              if (page === "ProcurementDetail") setPage("Procurement");
            }}>
              {page === "TaskDetail" ? "Tasks & Reports" :
               page === "OrderDetail" ? "Orders" :
               page === "DesignDetail" ? "Design" :
               page === "ProductionDetail" ? "Production" :
               page === "DispatchDetail" ? "Dispatch" :
               page === "DeliveryDetail" ? "Delivery" :
               page === "UserDetail" ? "Users & Roles" :
               page === "LeadDetail" ? "Leads" :
               page === "ClientDetail" ? "Clients" :
               page === "ProcurementDetail" ? "Procurement" :
               page === "Profile" ? "My Profile" :
               page}
            </span>
            {(page.includes("Detail") || page === "ApprovalPage") && (
              <>
                <ChevronRight size={14} />
                <span className="crumb">
                  {page === "TaskDetail" ? "Task Details" :
                   page === "OrderDetail" ? "Order Details" :
                   page === "DesignDetail" ? "Design Workflow" :
                   page === "ProductionDetail" ? "Production Stage" :
                   page === "DispatchDetail" ? "Dispatch Stage" :
                   page === "DeliveryDetail" ? "Delivery Tracking" :
                   page === "UserDetail" ? "User Profile" :
                   page === "LeadDetail" ? "Lead Details" :
                   page === "ClientDetail" ? "Client Details" :
                   page === "ProcurementDetail" ? "Request Details" :
                   "Review"}
                </span>
              </>
            )}
          </div>
          <div className="top-actions">
            <button className="global-search" data-testid="global-search-open" onClick={() => setSearchOpen(true)}>
              <Search size={15} /> Search orders, clients, invoices…
            </button>
            <button className="icon-btn" data-testid="notifications-button" onClick={() => setNotifOpen((v) => !v)}>
              <Bell size={18} />
              {unread > 0 && <i data-testid="notifications-badge">{unread}</i>}
            </button>

            {/* Profile Avatar Button & Dropdown immediately to the right of notifications bell */}
            <div className="profile-dropdown-wrap" ref={profileRef}>
              <button
                className="profile-avatar-btn"
                data-testid="profile-dropdown-trigger"
                aria-label="User profile menu"
                aria-haspopup="menu"
                aria-expanded={profileOpen}
                onClick={() => setProfileOpen((prev) => !prev)}
              >
                {user?.profile_image ? (
                  <img
                    src={user.profile_image}
                    alt={user.name || "User"}
                    className="avatar"
                    style={{ objectFit: "cover" }}
                  />
                ) : initials && initials !== "?" ? (
                  <div className="avatar">{initials}</div>
                ) : (
                  <div className="avatar"><User size={16} /></div>
                )}
              </button>

              {profileOpen && (
                <div
                  className="profile-dropdown-menu"
                  data-testid="profile-dropdown"
                  role="menu"
                  aria-orientation="vertical"
                >
                  <div className="profile-dropdown-header">
                    <div className="profile-dropdown-user">
                      <div className="avatar-lg">
                        {user?.profile_image ? (
                          <img
                            src={user.profile_image}
                            alt={user.name}
                            style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }}
                          />
                        ) : (
                          initials
                        )}
                      </div>
                      <div className="profile-dropdown-user-info">
                        <div className="profile-dropdown-user-name" title={user?.name}>{user?.name}</div>
                        <div className="profile-dropdown-user-email" title={user?.email}>{user?.email}</div>
                        <span
                          className="profile-dropdown-role-badge"
                          style={{
                            background: isSuperAdmin(user) ? "#f3e8ff" : isAdmin(user) ? "#dbeafe" : "#ecfdf5",
                            color: isSuperAdmin(user) ? "#6d28d9" : isAdmin(user) ? "#1d4ed8" : "#047857",
                            border: `1px solid ${isSuperAdmin(user) ? "#d8b4fe" : isAdmin(user) ? "#bfdbfe" : "#a7f3d0"}`,
                          }}
                        >
                          {isSuperAdmin(user) ? <ShieldAlert size={10} /> : isAdmin(user) ? <ShieldCheck size={10} /> : <User size={10} />}
                          {formatRole(user?.role)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="profile-dropdown-body">
                    {/* 1. My Profile — available to every authenticated user */}
                    <button
                      className="profile-dropdown-item"
                      data-testid="profile-dropdown-my-profile"
                      role="menuitem"
                      onClick={() => {
                        setProfileOpen(false);
                        go("Profile");
                      }}
                    >
                      <User size={16} />
                      <span>My Profile</span>
                    </button>

                    {/* 2. Settings — visible only to Super Admin */}
                    {isSuperAdmin(user) && (
                      <button
                        className="profile-dropdown-item"
                        data-testid="profile-dropdown-settings"
                        role="menuitem"
                        onClick={() => {
                          setProfileOpen(false);
                          go("Settings");
                        }}
                      >
                        <Cog size={16} />
                        <span>Settings</span>
                      </button>
                    )}

                    <div className="profile-dropdown-divider" />

                    {/* 3. Logout — available to authenticated users */}
                    <button
                      className="profile-dropdown-item logout-item"
                      data-testid="profile-dropdown-logout"
                      data-testid-legacy="logout-button"
                      id="profile-dropdown-logout-btn"
                      role="menuitem"
                      onClick={() => {
                        setProfileOpen(false);
                        onLogout();
                      }}
                    >
                      <LogOut size={16} />
                      <span>Log out</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {notifOpen && (
          <div className="notif-drawer" data-testid="notifications-drawer">
            <div className="notif-head">
              <b>Notifications</b>
              <button onClick={() => setNotifOpen(false)}><X size={16} /></button>
            </div>
            <div className="notif-list">
              {notifs.length === 0 && <div className="notif-empty">You're all caught up.</div>}
              {notifs.map((n) => (
                <button
                  key={n.notif_id}
                  className={`notif-item ${n.read ? "" : "unread"}`}
                  data-testid={`notification-${n.notif_id}`}
                  onClick={async () => {
                    await markRead(n.notif_id);
                    const lid = n.lead_id || (n.order_id && String(n.order_id).startsWith("LD-") ? n.order_id : null);
                    if (lid && onOpenLead) {
                      setNotifOpen(false);
                      onOpenLead(lid);
                    } else if (n.order_id && onOpenOrder) {
                      setNotifOpen(false);
                      onOpenOrder(n.order_id);
                    }
                  }}
                >
                  <span className="dot amber" />
                  <div><b>{n.message}</b><small>{new Date(n.created_at).toLocaleString()}</small></div>
                </button>
              ))}
            </div>
          </div>
        )}

        {searchOpen && (
          <div className="search-modal" data-testid="global-search-modal" onClick={() => setSearchOpen(false)}>
            <div className="search-panel" onClick={(e) => e.stopPropagation()}>
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search orders, clients, invoices, challans…" data-testid="global-search-input" />
              <div className="search-results">
                {results && Object.entries(results).map(([key, arr]) => (
                  arr.length > 0 && (
                    <div key={key} className="search-group">
                      <div className="small-label">{key.toUpperCase()}</div>
                      {arr.map((r, i) => (
                        <div
                          key={i}
                          className="search-hit"
                          data-testid={`search-hit-${key}-${i}`}
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            setSearchOpen(false);
                            setQ("");
                            if (key === "orders" && r.order_id && onOpenOrder) {
                              onOpenOrder(r.order_id);
                            } else if (key === "clients" && r.client_id && onOpenClient) {
                              onOpenClient(r.client_id);
                            } else if (key === "invoices") {
                              go("Invoices");
                            } else if (key === "challans") {
                              go("Challans");
                            } else if (onNavigate) {
                              go(key.charAt(0).toUpperCase() + key.slice(1));
                            }
                          }}
                        >
                          <b>{r.order_id || r.company_name || r.invoice_number || r.challan_number}</b>
                          <small>{r.client_name || r.contact_person || r.total || r.tracking_number}</small>
                        </div>
                      ))}
                    </div>
                  )
                ))}
                {q && results && Object.values(results).every((a) => a.length === 0) && (
                  <div className="notif-empty">No matches</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Independent Main Content Scrolling Container */}
        <div className="main-content-scroll" ref={scrollAreaRef} data-testid="main-content-scroll">
          {children}
        </div>
      </main>
    </div>
  );
}
